package com.evoapps.evoshift.domain.usecase.roster

import com.evoapps.evoshift.domain.model.RemovedShift
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.RosterPublishing
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.awaitsPublishing
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.util.AppClock
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus

/** SH-263: a published month's changes waiting for the next publish ([count]: shifts added, changed or removed; the earliest on [first]). */
data class UnpublishedChanges(val rosterId: String, val year: Int, val month: Int, val count: Int, val first: LocalDate)

/**
 * SH-263: which of [rosters] were published and have changes since ([RosterPublishing], the rule of
 * the builder's Publish button), live from the months' shifts: a publish clears them. Only months
 * that have not ended (a past month's plan is history; its own builder still says so). [first] is
 * the earliest changed day from today on (else the earliest at all).
 */
class ObserveUnpublishedChangesUseCase(
    private val shiftRepository: ShiftRepository,
    private val today: () -> LocalDate = { AppClock.today() }
) {
    operator fun invoke(rosters: List<Roster>): Flow<Map<String, UnpublishedChanges>> {
        val day = today()
        val watched = rosters.filter { RosterPublishing.wasPublished(it.status) && !ended(it, day) }
        if (watched.isEmpty()) return flowOf(emptyMap())
        return combine(watched.map { roster ->
            combine(
                shiftRepository.observeShiftsForRoster(roster.id).catch { emit(emptyList()) },
                shiftRepository.observeRemovedShifts(roster.id).catch { emit(emptyList()) }
            ) { shifts, removed -> changesOf(roster, shifts, removed, day) }
        }) { found -> found.filterNotNull().associateBy { it.rosterId } }.distinctUntilChanged()
    }

    private fun changesOf(roster: Roster, shifts: List<Shift>, removed: List<RemovedShift>, day: LocalDate): UnpublishedChanges? {
        val count = RosterPublishing.changes(roster.status, shifts, removed)
        if (count == 0) return null
        val dates = (shifts.filter { it.awaitsPublishing }.map { it.date } + removed.map { it.date })
            .mapNotNull { runCatching { LocalDate.parse(it) }.getOrNull() }.sorted()
        val first = dates.firstOrNull { it >= day } ?: dates.firstOrNull() ?: LocalDate(roster.year, roster.month, 1)
        return UnpublishedChanges(roster.id, roster.year, roster.month, count, first)
    }

    private fun ended(roster: Roster, day: LocalDate): Boolean =
        LocalDate(roster.year, roster.month, 1).plus(DatePeriod(months = 1)) <= day
}
